import React, { memo } from 'react';
import { useAppTranslation } from '@hooks';
import { getStateKeyByValue } from '@utils';
import { HitStateType } from '@definitions';
import { PresetTag } from '@components/display';
import { HitLogType, HitType } from '@pages/types';
import { Collapse, Icon, Row } from 'common-components';

import ContributorHitTask from './ContributorHitTask';
import ReviewHitTask from './ReviewHitTask';
import { DataLabel, DataRow, DataValue, HistoryRow, PanelContainer } from './SidebarRows';

import { store } from '@/store';

type HitSidebarTaskPropsType = {
  hit: HitType
};

const HitSidebarTaskComponent = ({ hit }: HitSidebarTaskPropsType) => {
  const { translate } = useAppTranslation();

  const hitLogs = store.use.hitLogs();

  return (
    <Collapse
      bordered={false}
      expandIconPlacement={'end'}
      expandIcon={({ isActive }) => (
        <Icon icon={isActive ? 'ChevronDown' : 'ChevronRight'} size={24} />
      )}
      defaultActiveKey={['contributor', 'review']}
      collapsible={'header'}
      items={[
        {
          key: 'contributor',
          label: translate('project.tasklineOverview.sidebar.contributor.title'),
          style: { paddingBottom: 10 },
          children: (
            <PanelContainer>
              {hit && (
                <Row>
                  <ContributorHitTask hit={hit} />
                </Row>
              )}
            </PanelContainer>
          ),
        },
        {
          key: 'metrics',
          label: translate('project.tasklineOverview.sidebar.taskMetrics.title'),
          style: { paddingBottom: 10, paddingTop: 10 },
          children: (
            <PanelContainer>
              {/*idTask*/}
              <DataRow>
                <DataLabel>{translate('project.tasklineOverview.sidebar.taskMetrics.taskId')}</DataLabel>
                <DataValue>{hit?.task.idTask}</DataValue>
              </DataRow>
              {/*request id*/}
              <DataRow>
                <DataLabel>{translate('project.tasklineOverview.sidebar.taskMetrics.requestId')}</DataLabel>
                <DataValue>{hit?.idHit}</DataValue>
              </DataRow>
              {/*status*/}
              <DataRow>
                <DataLabel>{translate('project.tasklineOverview.sidebar.taskMetrics.status')}</DataLabel>
                <PresetTag preset={'hit-state'} style={{ margin: 0 }} state={hit.hitState} withDot>
                  {translate(`common.states.hitState.${hit.hitState}`)}
                </PresetTag>
              </DataRow>
              {/*mark*/}
              <DataRow>
                <DataLabel>{translate('project.tasklineOverview.sidebar.taskMetrics.quality')}</DataLabel>
                <DataValue>{hit?.mark ?? '-'}</DataValue>
              </DataRow>
              {/*duration*/}
              <DataRow>
                <DataLabel>{translate('project.tasklineOverview.sidebar.taskMetrics.duration')}</DataLabel>
                <DataValue>{hit?.duration ?? '-'}</DataValue>
              </DataRow>
              {/*effectiveTime*/}
              <DataRow>
                <DataLabel>{translate('project.tasklineOverview.sidebar.taskMetrics.effectiveTime')}</DataLabel>
                <DataValue>{hit?.effectiveTime ?? '-'}</DataValue>
              </DataRow>
              {/*endDate*/}
              <DataRow>
                <DataLabel>{translate('project.tasklineOverview.sidebar.taskMetrics.submissionDate')}</DataLabel>
                <DataValue>{hit?.endDate ?? '-'}</DataValue>
              </DataRow>
              {/*creationDate*/}
              <DataRow>
                <DataLabel>{translate('project.tasklineOverview.sidebar.taskMetrics.creationDate')}</DataLabel>
                <DataValue>{hit?.creationDate ?? '-'}</DataValue>
              </DataRow>
            </PanelContainer>
          ),
        },
        {
          key: 'history',
          label: translate('project.tasklineOverview.sidebar.history.title'),
          style: { paddingBottom: 10, paddingTop: 10 },
          children: (
            <PanelContainer>
              {hitLogs?.length &&
                hitLogs.map((log: HitLogType, i) => (
                  <HistoryRow
                    key={`${log.creationDate}-${log.event}-${i}`}
                    date={log.creationDate}
                    event={translate(`common.states.eventType.${log.event}`)}
                    user={log.user}
                  />
                ))}
            </PanelContainer>
          ),
        },
        ...(hit?.hitState === getStateKeyByValue(HitStateType, 'completed')
          ? [
            {
              key: 'review',
              label: translate('project.tasklineOverview.sidebar.review.title'),
              style: { paddingTop: 10 },
              children: (
                <PanelContainer>
                  <ReviewHitTask hit={hit} />
                </PanelContainer>
              ),
            },
          ]
          : []),
      ]}
    />
  );
};

const HitSidebarTask = memo(
  HitSidebarTaskComponent,
  (oldProps: HitSidebarTaskPropsType, newProps: HitSidebarTaskPropsType) => {
    return JSON.stringify(oldProps) === JSON.stringify(newProps);
  },
);

export default HitSidebarTask;
